import React, { useCallback, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, Typography } from '@mui/material';
import { CloudUpload, UploadFile } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAIRequests } from '../../context/AIRequestContext';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { useUsage } from '../../context/UsageContext';
import { hasReachedLimit } from '../../services/usageService';
import { PRESENTATION_ACCEPT, startPresentationImport } from '../../services/presentationUploadService';
import { templateEditorPath } from '../../utils/navigation';

/**
 * Import a PowerPoint master (.pptx or .potx, up to 50 MB) by dropping it here or choosing it.
 *
 * The file is checked in the browser, the template row is created as 'importing' and the editor
 * opens on it straight away (replacing this "new template" page) with live progress, while the
 * bytes upload unchanged to Storage and the server import runs.
 *
 * @param {object} [props] - Props (all optional).
 * @param {function(object): void} [props.onDispatched] - Called with the new template once its row exists.
 * @param {string} [props.name] - Template name typed on the new-template page (defaults to the file name).
 * @param {string} [props.description] - Description typed on the new-template page.
 * @param {() => boolean} [props.confirmReplace] - Returns true when the page has slides built by
 *   hand that the import would replace; the user is asked first.
 * @returns {React.ReactElement}
 */
function PowerPointUploadSection({ onDispatched, name, description, confirmReplace }) {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { actions } = useApp();
  const { createRequest } = useAIRequests();
  const { state: authState } = useAuth();
  const { refreshUsage } = useUsage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  /** File waiting for "Import and replace" (the page has hand-built slides). */
  const [pendingFile, setPendingFile] = useState(null);

  /**
   * Check quota and the file, create the importing template and open it.
   * @param {File|undefined} file - Chosen file.
   * @returns {Promise<void>}
   */
  const importFile = useCallback(
    async (file) => {
      if (!file || busy) return;
      const accountId = authState?.activeAccount?.id;
      if (!accountId) {
        setError('No active workspace. Choose a workspace and try again.');
        return;
      }
      setBusy(true);
      setError('');
      try {
        const usage = typeof refreshUsage === 'function' ? await refreshUsage() : null;
        if (usage && hasReachedLimit(usage, 'templates')) {
          setError('You have reached your plan\'s template limit. Delete a template or upgrade your plan to import another.');
          return;
        }
        await startPresentationImport({
          file,
          accountId,
          name,
          description,
          createTemplate: actions.createTemplate,
          deleteTemplate: actions.deleteTemplate,
          createRequest,
          onCreated: (template) => {
            if (typeof onDispatched === 'function') onDispatched(template);
            navigate(templateEditorPath({ templateId: template.id, kind: 'powerpoint-rich' }), { replace: true });
          },
        });
      } catch (err) {
        setError(err?.message || 'Could not start the import. Try again.');
      } finally {
        setBusy(false);
      }
    },
    [busy, authState?.activeAccount?.id, refreshUsage, actions, createRequest, onDispatched, navigate, name, description]
  );

  /**
   * Start an import, asking first when it would replace slides built on this page.
   * @param {File|undefined} file - Chosen or dropped file.
   * @returns {void}
   */
  const requestImport = (file) => {
    if (!file || busy) return;
    if (typeof confirmReplace === 'function' && confirmReplace()) {
      setPendingFile(file);
      return;
    }
    importFile(file);
  };

  const onInputChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    requestImport(file);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragActive(false);
  };
  const onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    requestImport(event.dataTransfer?.files?.[0]);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <input
        ref={inputRef}
        type="file"
        accept={PRESENTATION_ACCEPT}
        hidden
        onChange={onInputChange}
        data-testid="pptx-upload-input"
      />
      {/* A drop target for the mouse; the "Choose file" button is the one keyboard entry point. */}
      <Box
        data-testid="pptx-drop-zone"
        onClick={() => !busy && inputRef.current?.click()}
        onDragEnter={onDragOver}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        sx={{
          p: { xs: 2, sm: 2.5 },
          border: '1.5px dashed',
          borderColor: dragActive ? 'var(--q-accent)' : 'var(--q-border-hover)',
          borderRadius: 'var(--q-radius-card)',
          bgcolor: dragActive ? 'var(--q-accent-tint)' : 'var(--q-bg-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          cursor: busy ? 'default' : 'pointer',
          opacity: busy ? 0.7 : 1,
          transition: 'border-color 120ms ease, background-color 120ms ease',
          '&:hover': { borderColor: 'var(--q-accent)' },
        }}
      >
        <CloudUpload sx={{ color: 'var(--q-accent)' }} />
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography sx={{ fontWeight: 600 }}>Drop a PowerPoint master here</Typography>
          <Typography variant="body2" color="text.secondary">
            .pptx or .potx, up to 50 MB. Your original file is kept unchanged.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadFile />}
          disabled={busy}
          aria-label="Choose a PowerPoint file to import"
          onClick={(event) => {
            event.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Choose file
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Inky reads the slides and suggests an AI prompt for each <code>{'{{variable}}'}</code>. The editor opens
        right away and shows the progress.
      </Typography>
      {busy && <LinearProgress sx={{ mt: 1 }} aria-label="Checking the file" />}
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      <Dialog open={Boolean(pendingFile)} onClose={() => setPendingFile(null)} aria-labelledby="pptx-replace-title">
        <DialogTitle id="pptx-replace-title">Replace the slides on this page?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Importing {pendingFile?.name || 'this file'} opens a new template with the slides from the file. The slides you
            built on this page are not saved and will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingFile(null)}>Keep editing</Button>
          <Button
            variant="contained"
            onClick={() => {
              const file = pendingFile;
              setPendingFile(null);
              importFile(file);
            }}
          >
            Import and replace
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PowerPointUploadSection;
